/** */
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setPageTitle } from 'store/actions/header';
import { PageContentWrapper } from 'app/layouts/wrapper/page-content';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { Spacer } from 'app/layouts/generic';
import { useOrganizationService } from 'hooks/organization';
import { useAgencySalesService } from 'hooks/sales/agency';
import { useCurrencyService } from 'hooks/sales/currency';
import { useCreateOrganization } from 'contexts/organization';
import { toReadableNumber } from 'app/shared/utils/number';
import useScreenSize from 'hooks/size';
import { OnboardingChecklist } from './sections/onboarding';
import { MetricsBar } from './sections/metrics';
import { ExpiringSubscriptions } from './sections/expiring';
import { ActiveSubscriptions } from './sections/active';
import { Guides } from './sections/guides';
import { Welcome } from './sections/welcome';
import styles from './dashboard.module.css';

const DAY_MS = 86400000;
const DEFAULT_CURRENCY = 'NGN';

const EMPTY_STATS = {
  total_earnings: 0,
  pending_earnings: 0,
  settled_earnings: 0,
  total_spends: 0,
  active_subscriptions: [],
  expiring_subscriptions: []
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
};

// Days from now until a subscription's expiry. Negative means already lapsed.
const daysUntil = (expires_at) => {
  const ts = new Date(expires_at).getTime();
  if (isNaN(ts)) return 0;
  return Math.max(0, Math.ceil((ts - Date.now()) / DAY_MS));
};

// Shape a subscription record into the row both subscription lists render.
const toSubscriptionItem = (sub, org_names, money) => {
  const amount = sub.price?.amount ?? sub.cost;
  const currency_code = sub.price?.currency?.code ?? sub.currency?.code ?? sub.currency;
  // The subscription is tied to a client org by its tenant id; resolve the org's
  // name from the list we already fetched, with a few shape fallbacks.
  const tenant_id = sub.tenant_id ?? sub.tenant?.id ?? sub.org_id;

  return {
    id: sub.id,
    client_name:
      org_names[tenant_id] || sub.tenant?.name || sub.tenant_name || sub.client?.name || 'Client',
    plan: sub.title || sub.tier || 'Subscription',
    cost_label: amount != null ? money(amount, currency_code) : null,
    days_left: daysUntil(sub.expires_at),
    expires_label: new Date(sub.expires_at).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })
  };
};

const GMDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const { firstname } = useSelector((state) => state.user_data);
  const agency = useSelector((state) => state.metadata.agency);

  const { fetchOrganizations } = useOrganizationService();
  const { fetchStats, fetchActiveSubscriptions } = useAgencySalesService();
  const { getCurrencySymbol } = useCurrencyService();
  const { openCreateOrganization } = useCreateOrganization();

  const [loading, setLoading] = useState(true);
  const [client_count, setClientCount] = useState(0);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [expiring, setExpiring] = useState([]);
  const [active_subscriptions, setActiveSubscriptions] = useState([]);

  const track = agency?.track;
  const currency_symbol = getCurrencySymbol(DEFAULT_CURRENCY) || '₦';

  const load = useCallback(async () => {
    if (!agency?.id) return;
    setLoading(true);

    // The stats endpoint is scoped to the agency's orgs, so fetch the org list
    // first to get their ids (and names, for labelling expiring subscriptions).
    const { orgs: org_list, size } = await fetchOrganizations({
      query_string: `agent_id=${agency.id}`
    });
    const orgs = org_list || [];
    const org_ids = orgs.map((org) => org.id).join(',');
    const org_scope = `org_ids=${org_ids}`;

    const [{ stats: fetched }, { subscriptions: active }] = await Promise.all([
      fetchStats({ query_string: `agency_id=${agency.id}&${org_scope}` }),
      fetchActiveSubscriptions({ query_string: org_scope })
    ]);
    const next_stats = fetched || EMPTY_STATS;

    const org_names = orgs.reduce((map, org) => ({ ...map, [org.id]: org.name }), {});
    const money = (amount, code) =>
      `${getCurrencySymbol(code) || currency_symbol}${toReadableNumber(amount)}`;

    const expiring_soon = (next_stats.expiring_subscriptions || [])
      .map((sub) => toSubscriptionItem(sub, org_names, money))
      .sort((a, b) => a.days_left - b.days_left);

    const active_list = (active || [])
      .map((sub) => toSubscriptionItem(sub, org_names, money))
      .sort((a, b) => a.days_left - b.days_left);

    setClientCount(size || orgs.length);
    setStats(next_stats);
    setExpiring(expiring_soon);
    setActiveSubscriptions(active_list);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agency]);

  useEffect(() => {
    dispatch(
      setPageTitle([
        { title: '', path: '' },
        { title: '', path: '' }
      ])
    );
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const goToOrganisations = () => navigate('/organizations');
  const createOrganisation = () => openCreateOrganization({ onCreated: load });

  const has_clients = client_count > 0;
  const has_subscription = active_subscriptions.length > 0;
  // Once both onboarding steps are done there's nothing left to nudge — drop the
  // checklist entirely (keep it visible while loading so it doesn't flash in).
  const show_checklist = loading || !(has_clients && has_subscription);

  return (
    <PageContentWrapper className={styles.wrapper}>
      {isMobile && <Spacer multiple={2} />}
      {firstname && (
        <>
          <p className={styles.salutation}>
            Good {getGreeting()}, {firstname}
          </p>
          <Spacer multiple={4} />
        </>
      )}

      <GridRow num_of_columns={5} mobileStyles={{ gap: 0 }}>
        <GridColumn span={3}>
          {show_checklist && (
            <>
              <OnboardingChecklist
                has_clients={has_clients}
                has_subscription={has_subscription}
                loading={loading}
                onCreateOrganisation={createOrganisation}
                onViewOrganisations={goToOrganisations}
              />
              <Spacer multiple={3} />
            </>
          )}
          <MetricsBar
            track={track}
            loading={loading}
            client_count={client_count}
            total_earnings={stats.total_earnings}
            pending_earnings={stats.pending_earnings}
            total_spend={stats.total_spends}
            currency_symbol={currency_symbol}
          />
          <Spacer multiple={3} />
          <ExpiringSubscriptions items={expiring} loading={loading} onView={goToOrganisations} />
          <Spacer multiple={3} />
          <ActiveSubscriptions
            items={active_subscriptions}
            loading={loading}
            onView={goToOrganisations}
          />
        </GridColumn>

        <GridColumn span={2}>
          <Welcome />
          <Spacer multiple={3} />
          <Guides />
        </GridColumn>
      </GridRow>

      <Spacer multiple={4} />
    </PageContentWrapper>
  );
};

export default GMDashboard;
